import { useEffect, useState } from "react";
import api, { apiErr } from "../lib/api";
import { toast } from "sonner";
import { VEHICLE_CATEGORIES } from "../lib/constants";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Checkbox } from "../components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Plus, Pencil, Trash2, Truck, User, Snowflake, Sun } from "lucide-react";

const FEATURES = { sponda_idraulica: "Sponda idraulica", muletto: "Muletto" };

export default function Fleet() {
  return (
    <div className="space-y-5">
      <h1 className="font-display text-3xl text-lm-text">Mezzi &amp; Autisti</h1>
      <Tabs defaultValue="vehicles">
        <TabsList className="bg-lm-sandsoft">
          <TabsTrigger value="vehicles" data-testid="tab-vehicles">Mezzi</TabsTrigger>
          <TabsTrigger value="drivers" data-testid="tab-drivers">Autisti</TabsTrigger>
        </TabsList>
        <TabsContent value="vehicles"><Vehicles /></TabsContent>
        <TabsContent value="drivers"><Drivers /></TabsContent>
      </Tabs>
    </div>
  );
}

const EMPTY_V = {
  category: "Furgone", identifier: "", plate: "", cargo_length_cm: "", cargo_width_cm: "", cargo_height_cm: "",
  capacity_kg: "", capacity_m3: "", covered: true, features: [], base_label: "Sede Mariano Comense",
  base_address: "Via Montesanto 12, Mariano Comense", base_lat: 45.6975, base_lng: 9.1780, available: true, downtime: "", notes: "",
};

function Vehicles() {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [ed, setEd] = useState(null);
  const load = () => api.get("/vehicles").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const set = (k, v) => setEd((e) => ({ ...e, [k]: v }));

  const save = async () => {
    const body = { ...ed };
    ["cargo_length_cm", "cargo_width_cm", "cargo_height_cm", "capacity_kg", "capacity_m3", "base_lat", "base_lng"].forEach((k) => { body[k] = body[k] === "" ? null : parseFloat(body[k]); });
    try { if (ed.id) await api.put(`/vehicles/${ed.id}`, body); else await api.post("/vehicles", body); toast.success("Mezzo salvato"); setOpen(false); load(); } catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (v) => { if (!window.confirm(`Eliminare ${v.identifier}?`)) return; await api.delete(`/vehicles/${v.id}`); toast.success("Eliminato"); load(); };

  return (
    <div className="space-y-4 mt-4">
      <div className="flex justify-end"><Button data-testid="new-vehicle-btn" onClick={() => { setEd({ ...EMPTY_V }); setOpen(true); }} className="bg-lm-brown hover:bg-lm-brownh text-lm-cream"><Plus className="w-4 h-4 mr-2" /> Nuovo mezzo</Button></div>
      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4">
        {rows.map((v) => (
          <div key={v.id} data-testid={`vehicle-${v.identifier}`} className="lm-card p-5">
            <div className="flex items-start justify-between">
              <div>
                <span className="text-[10px] uppercase tracking-wider font-bold text-lm-muted">{v.category}</span>
                <h3 className="font-display text-xl text-lm-brown">{v.identifier}</h3>
                <p className="text-xs text-lm-muted font-mono">{v.plate || "senza targa"}</p>
              </div>
              <Truck className="w-6 h-6 text-lm-sand" />
            </div>
            <div className="mt-3 flex flex-wrap gap-1.5 text-xs">
              <Tag>{v.capacity_kg ? `${v.capacity_kg} kg` : "portata n.d."}</Tag>
              <Tag>{v.capacity_m3 ? `${v.capacity_m3} m³` : "vol. n.d."}</Tag>
              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border ${v.covered ? "bg-[#EFF7F2] text-[#166534] border-[#9AD4AC]" : "bg-[#FEF8EC] text-[#9A6B00] border-[#F3D295]"}`}>
                {v.covered ? <Snowflake className="w-3 h-3" /> : <Sun className="w-3 h-3" />}{v.covered ? "Coperto" : "Scoperto"}
              </span>
              {(v.features || []).map((f) => <Tag key={f}>{FEATURES[f] || f}</Tag>)}
              <span className={`px-2 py-0.5 rounded-md border ${v.available ? "bg-[#EFF7F2] text-[#166534] border-[#9AD4AC]" : "bg-[#FDF2F2] text-[#991B1B] border-[#F8B4B4]"}`}>{v.available ? "Disponibile" : "Fermo"}</span>
            </div>
            {v.notes && <p className="text-sm text-lm-text2 mt-2">{v.notes}</p>}
            <div className="flex gap-2 mt-3">
              <Button size="sm" variant="outline" onClick={() => { setEd({ ...EMPTY_V, ...v, features: v.features || [] }); setOpen(true); }} className="border-lm-sand text-lm-brown"><Pencil className="w-4 h-4 mr-1.5" /> Modifica</Button>
              <Button size="sm" variant="outline" onClick={() => del(v)} className="border-lm-sand text-[#991B1B]"><Trash2 className="w-4 h-4" /></Button>
            </div>
          </div>
        ))}
      </div>

      {open && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-xl max-h-[92vh] overflow-y-auto bg-lm-cream" data-testid="vehicle-dialog">
            <DialogHeader><DialogTitle className="font-display text-2xl text-lm-brown">{ed.id ? "Modifica mezzo" : "Nuovo mezzo"}</DialogTitle></DialogHeader>
            <div className="grid sm:grid-cols-2 gap-3 py-2">
              <div><Label className="text-xs text-lm-text2">Categoria</Label>
                <Select value={ed.category} onValueChange={(v) => set("category", v)}><SelectTrigger data-testid="v-category" className="mt-1 bg-white"><SelectValue /></SelectTrigger><SelectContent>{VEHICLE_CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
              </div>
              <F label="Identificativo"><Input data-testid="v-identifier" value={ed.identifier} onChange={(e) => set("identifier", e.target.value)} className="bg-white" /></F>
              <F label="Targa"><Input value={ed.plate || ""} onChange={(e) => set("plate", e.target.value)} className="bg-white" /></F>
              <F label="Portata (kg)"><Input type="number" value={ed.capacity_kg} onChange={(e) => set("capacity_kg", e.target.value)} className="bg-white" /></F>
              <F label="Capacità (m³)"><Input type="number" step="0.1" value={ed.capacity_m3} onChange={(e) => set("capacity_m3", e.target.value)} className="bg-white" /></F>
              <F label="Vano L×W×H (cm)"><div className="flex gap-1"><Input placeholder="L" value={ed.cargo_length_cm} onChange={(e) => set("cargo_length_cm", e.target.value)} className="bg-white" /><Input placeholder="W" value={ed.cargo_width_cm} onChange={(e) => set("cargo_width_cm", e.target.value)} className="bg-white" /><Input placeholder="H" value={ed.cargo_height_cm} onChange={(e) => set("cargo_height_cm", e.target.value)} className="bg-white" /></div></F>
              <F label="Sede partenza/rientro"><Input value={ed.base_label || ""} onChange={(e) => set("base_label", e.target.value)} className="bg-white" /></F>
              <F label="Indirizzo sede"><Input value={ed.base_address || ""} onChange={(e) => set("base_address", e.target.value)} className="bg-white" /></F>
              <div className="sm:col-span-2 space-y-2">
                <label className="flex items-center gap-3 bg-white border border-lm-sandsoft rounded-lg px-3 py-2.5"><Switch data-testid="v-covered" checked={ed.covered} onCheckedChange={(v) => set("covered", v)} /><span className="text-sm">Vano <b>al coperto</b> (idoneo a merce da proteggere). Il Furgone Cassonato è normalmente scoperto.</span></label>
                <label className="flex items-center gap-3 bg-white border border-lm-sandsoft rounded-lg px-3 py-2.5"><Switch checked={ed.available} onCheckedChange={(v) => set("available", v)} /><span className="text-sm">Disponibile</span></label>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(FEATURES).map(([k, v]) => (
                    <label key={k} className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm cursor-pointer ${ed.features.includes(k) ? "bg-lm-brown text-lm-cream border-lm-brown" : "bg-white border-lm-sandsoft"}`}>
                      <Checkbox checked={ed.features.includes(k)} onCheckedChange={() => set("features", ed.features.includes(k) ? ed.features.filter((x) => x !== k) : [...ed.features, k])} className="border-lm-sand data-[state=checked]:bg-lm-cream data-[state=checked]:text-lm-brown" />{v}
                    </label>
                  ))}
                </div>
              </div>
              <div className="sm:col-span-2"><F label="Note / dotazioni / vincoli"><Textarea value={ed.notes || ""} onChange={(e) => set("notes", e.target.value)} className="bg-white" rows={2} /></F></div>
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setOpen(false)} className="border-lm-sand text-lm-brown">Annulla</Button><Button data-testid="save-vehicle-btn" onClick={save} className="bg-lm-brown hover:bg-lm-brownh text-lm-cream">Salva</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function Drivers() {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/users?role=autista").then((r) => setRows(r.data)).catch(() => {}); }, []);
  return (
    <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mt-4">
      {rows.length === 0 && <p className="text-lm-muted text-sm">Nessun autista. Aggiungili dalla sezione Utenti.</p>}
      {rows.map((d) => (
        <div key={d.id} data-testid={`driver-${d.id}`} className="lm-card p-5">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-lm-sandsoft flex items-center justify-center text-lm-brown font-bold">{d.name?.[0]}</div>
            <div><h3 className="font-semibold text-lm-text">{d.name}</h3><p className="text-xs text-lm-muted">{d.email}</p></div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2 text-xs">
            <span className={`px-2 py-0.5 rounded-md border ${d.available ? "bg-[#EFF7F2] text-[#166534] border-[#9AD4AC]" : "bg-[#FDF2F2] text-[#991B1B] border-[#F8B4B4]"}`}>{d.available ? "Disponibile" : "Non disponibile"}</span>
            {(d.work_start || d.work_end) && <span className="px-2 py-0.5 rounded-md bg-lm-sandsoft text-lm-brown">{d.work_start || "?"}–{d.work_end || "?"}</span>}
            {d.phone && <span className="px-2 py-0.5 rounded-md bg-lm-sandsoft text-lm-brown">{d.phone}</span>}
          </div>
          {d.notes && <p className="text-sm text-lm-text2 mt-2">{d.notes}</p>}
        </div>
      ))}
    </div>
  );
}

const Tag = ({ children }) => <span className="px-2 py-0.5 rounded-md bg-lm-sandsoft text-lm-brown">{children}</span>;
const F = ({ label, children }) => <div><Label className="text-xs text-lm-text2">{label}</Label><div className="mt-1">{children}</div></div>;
